import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Shield, Clock, Globe, Lock, ArrowRight, Play, ClipboardCheck, Compass, Handshake, Wrench, Mail } from "lucide-react";
import { SiteFooter } from "@/components/SiteFooter";

const HERO = "https://images.unsplash.com/photo-1779700210487-a01758a3c55a?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000";
const TEXTURE = "https://images.unsplash.com/photo-1644088379091-d574269d422f?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";

const SELF_ASSESSMENT_URL = "/assessment";

const stages = [
  { icon: ClipboardCheck, k: "01", t: "Start with a self-assessment", d: "Not every organisation needs what we offer. Our self-assessment helps you understand where you are today, how prepared you feel for disruption, and whether a simulation exercise is the right next step. It's a simple way to explore your current strengths, identify gaps and decide if we'd be a good fit to work together.", link: { href: SELF_ASSESSMENT_URL, label: "Take the self-assessment" } },
  { icon: Compass, k: "02", t: "Guided simulations", d: "You won't be left to navigate the journey alone. Every simulation includes a dedicated advisor who'll take time to understand your organisation, tailor the scenario around your circumstances and support your team throughout the experience. Following the exercise, we'll help you turn your insights into practical actions that can strengthen resilience immediately." },
  { icon: Handshake, k: "03", t: "Shared resilience exercises", d: "Many disruptions don't stop at organisational boundaries. If you work closely with another organisation, supplier, partner or community group, we can design joint exercises that allow everyone to explore a shared scenario together. These sessions help reveal dependencies, improve coordination and strengthen understanding between organisations before a real incident occurs." },
  { icon: Wrench, k: "04", t: "Implementation support", d: "Knowing what needs to change is only half the challenge. For organisations that need additional support, we offer short-term project management services to help turn exercise outcomes into meaningful action. This gives you access to experienced support without increasing permanent overheads or stretching existing teams." },
];

const disruptions = [
  "cyber security", "ransomware", "natural disaster", "civil unrest",
  "workforce crisis", "power outages", "vendor or supply chain failure", "on site crisis",
];

const StageCard = ({ s, i }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const pointer = useRef("mouse");
  const reveal = (v) => {
    setOpen(v);
    if (!v) return;
    setTimeout(() => {
      const r = ref.current?.getBoundingClientRect();
      if (r && r.bottom > window.innerHeight) window.scrollBy({ top: Math.min(r.bottom - window.innerHeight + 24, r.top - 80), behavior: "smooth" });
    }, 320);
  };
  return (
    <motion.div ref={ref} data-testid={`how-card-${s.k}`} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }} transition={{ delay: i * 0.08 }}
      role="button" tabIndex={0} aria-expanded={open}
      onPointerDown={(e) => { pointer.current = e.pointerType; }}
      onPointerEnter={(e) => e.pointerType === "mouse" && reveal(true)}
      onPointerLeave={(e) => e.pointerType === "mouse" && setOpen(false)}
      onClick={() => pointer.current !== "mouse" && reveal(!open)}
      onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && (e.preventDefault(), reveal(!open))}
      className={`p-8 transition-colors flex flex-col cursor-pointer ${open ? "bg-[var(--bg-elevated)]" : "bg-[var(--bg-surface)]"}`}>
      <s.icon className={`w-7 h-7 mb-6 transition-colors shrink-0 ${open ? "text-[var(--urgent)]" : "text-white"}`} />
      <div className="font-mono text-xs text-[var(--txt-muted)] mb-2">{s.k}</div>
      <h3 className="font-display font-bold text-[#D6D9DD]" style={{ fontSize: "22px" }}>{s.t}</h3>
      <div className={`grid transition-[grid-template-rows,opacity,margin] duration-300 ease-out ${open ? "grid-rows-[1fr] opacity-100 mt-3" : "grid-rows-[0fr] opacity-0"}`}>
        <div data-testid={`how-card-${s.k}-body`} className="overflow-hidden text-white leading-relaxed" style={{ fontSize: "18px" }}>
          {s.d}
          {s.link && <a href={s.link.href} data-testid="self-assessment-link" onClick={(e) => e.stopPropagation()} className="mt-4 flex items-center gap-2 text-[var(--urgent)] font-medium hover:underline">{s.link.label} <ArrowRight className="w-4 h-4" /></a>}
        </div>
      </div>
    </motion.div>
  );
};

export default function Welcome() {
  const nav = useNavigate();
  return (
    <div className="min-h-screen bg-[var(--bg-base)] text-white overflow-x-hidden">
      {/* Nav */}
      <header className="glass fixed top-0 inset-x-0 z-50 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <button onClick={() => nav("/")} data-testid="nav-home-btn" className="flex items-center gap-2 hover:opacity-80 transition-opacity">
            <div className="w-7 h-7 rounded-sm bg-white flex items-center justify-center"><Shield className="w-4 h-4 text-black" /></div>
            <span className="font-display font-extrabold text-lg tracking-tight">TARDIGRADE</span>
          </button>
          <button data-testid="nav-login-btn" onClick={() => nav("/login")}
            className="group flex items-center gap-2 bg-white text-black px-4 py-2 text-sm font-medium hover:bg-gray-200 transition-colors">
            <Lock className="w-3.5 h-3.5" /> Log in
          </button>
        </div>
      </header>

      {/* Hero */}
      <section className="relative min-h-screen flex items-center pt-16">
        <div className="absolute inset-0">
          <img src={HERO} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-[var(--bg-base)]/85" />
          <div className="absolute inset-0 bg-gradient-to-t from-[var(--bg-base)] via-transparent to-[var(--bg-base)]/60" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 w-full">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} className="max-w-5xl">
            <h1 className="font-display font-extrabold text-4xl sm:text-6xl lg:text-7xl tracking-tight leading-[0.95] mb-6">
              Resilience is not paperwork,<br /><span className="text-[var(--urgent)]">it's survival.</span>
            </h1>
            <div data-testid="hero-subtext" className="text-white max-w-4xl leading-relaxed mb-10 space-y-4 bg-black/40 backdrop-blur-[2px] -mx-5 px-5 py-4 rounded-sm" style={{ fontSize: "20px" }}>
              <p>Most organisations already have resilience in their bones: the improvised workarounds, the “we’d figure it out on the day” conversations, the quiet knowledge of who steps up when things wobble. We’re here for exactly those teams, the ones who don’t need a thick binder or a bundle of documents that collects dust, but want something clearer, calmer and more reliable.</p>
              <p>Our simulations turn your existing instincts into practical confidence, helping you test what would really happen in a disruption and strengthen the informal arrangements you already trust.</p>
            </div>
            <div className="flex flex-wrap gap-4">
              <a href="#how" className="flex items-center gap-2 border border-white/20 px-6 py-3.5 font-medium hover:bg-white/10 transition-colors">
                How it works
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Scenario carousel strip */}
      <section className="border-y border-white/10 bg-[var(--bg-surface)] overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center gap-6">
          <span className="shrink-0 hidden sm:block text-[var(--txt-secondary)]" style={{ fontSize: "14px" }}>Common scenarios</span>
          <div className="relative flex-1 overflow-hidden marquee-mask">
            <div className="flex w-max animate-marquee">
              {[0, 1].map((g) => (
                <ul key={g} aria-hidden={g === 1} className="flex items-center gap-8 pr-8 shrink-0">
                  {disruptions.map((s, i) => (
                    <li key={i} className="flex items-center gap-2.5 text-[var(--txt-secondary)] font-mono shrink-0" style={{ fontSize: "14px" }}>
                      <span className="w-2 h-2 bg-[var(--urgent)] shrink-0" />{s}
                    </li>
                  ))}
                </ul>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* How it works - bento */}
      <section id="how" className="max-w-7xl mx-auto px-6 py-24">
        <h2 data-testid="what-to-expect-title" className="font-display font-bold text-3xl sm:text-5xl tracking-tight mb-6">What to expect</h2>
        <p data-testid="what-to-expect-text" className="text-[var(--txt-secondary)] leading-relaxed mb-16 max-w-5xl" style={{ fontSize: "20px" }}>Working with us is practical, collaborative and refreshingly straightforward. Together, we'll explore realistic scenarios in a safe environment, uncover strengths you may not realise you already have, and identify weaknesses before they become problems. You'll leave with a clearer understanding of how your people, processes and decisions hold up under pressure, along with practical actions that strengthen resilience where it matters most.</p>
        <h2 className="font-display font-bold text-3xl sm:text-5xl tracking-tight mb-10">How we work</h2>
        <div className="grid md:grid-cols-2 xl:grid-cols-4 gap-px bg-white/10 border border-white/10 items-stretch">
          {stages.map((s, i) => <StageCard key={s.k} s={s} i={i} />)}
        </div>
      </section>

      {/* Texture band */}
      <section className="relative py-24 border-y border-white/10">
        <div className="absolute inset-0"><img src={TEXTURE} alt="" className="w-full h-full object-cover opacity-30" /><div className="absolute inset-0 bg-[var(--bg-base)]/80" /></div>
        <div className="relative max-w-4xl mx-auto px-6 text-center">
          <Globe className="w-8 h-8 mx-auto mb-6 text-[var(--urgent)]" />
          <h2 data-testid="cta-band-title" className="font-display font-bold text-3xl sm:text-4xl tracking-tight">Discover how your people, processes and decisions perform when conditions change.</h2>
          <a href="mailto:support@tardigradeteam.com" data-testid="start-conversation-btn"
            className="inline-flex items-center gap-2 mt-10 bg-white text-black px-8 py-4 font-medium hover:bg-gray-200 transition-colors">
            <Mail className="w-4 h-4" /> Start the conversation
          </a>
        </div>
      </section>

      {/* Videos placeholder */}
      <section className="max-w-7xl mx-auto px-6 py-24">
        <div className="text-[var(--txt-secondary)] uppercase tracking-[0.2em] mb-8 font-medium" style={{ fontSize: "22px" }}>Watch</div>
        <div className="grid md:grid-cols-2 gap-px bg-white/10 border border-white/10">
          {[1, 2].map((i) => (
            <div key={i} className="bg-[var(--bg-surface)] aspect-video flex flex-col items-center justify-center gap-3 text-[var(--txt-muted)]">
              <div className="w-14 h-14 rounded-full border border-white/20 flex items-center justify-center"><Play className="w-5 h-5" /></div>
              <span className="font-mono text-sm">Video coming soon</span>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="border-t border-white/10 bg-[var(--bg-surface)]">
        <div className="max-w-7xl mx-auto px-6 py-20 flex flex-col md:flex-row items-center justify-between gap-8">
          <div>
            <h2 className="font-display font-bold text-3xl tracking-tight mb-2">Ready to enter your simulation?</h2>
            <p className="text-[var(--txt-secondary)] flex items-center gap-2"><Clock className="w-4 h-4" /> Secure access for registered participants only.</p>
          </div>
          <button data-testid="cta-login-btn" onClick={() => nav("/login")}
            className="bg-white text-black px-8 py-4 font-medium hover:bg-gray-200 transition-colors flex items-center gap-2">
            Log in <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
}
